import { debounce } from 'lodash';
import { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import RichText from '../RichText/RichText';
import { useStudentChunkNote } from '../../hooks';
import { Button, SaveStatus } from 'edrolo-style-guide';
import { ANALYTICS_CONSTANTS } from '../../constants';
import { ChunkNote } from '../../types/Hooks';
import { useAnalytics } from 'edrolo/analytics';
import styles from './StudentNote.scss';

const StudentNote = ({
  chunkId,
  notebookUrl,
  disableNotebook,
}: StudentNoteProps): JSX.Element => {
  const { data, loading, error, status, updateStudentChunkNote } =
    useStudentChunkNote(chunkId);

  const { track } = useAnalytics({ throttle: 60000 });

  const debouncedUpdateStudentChunkNote = useMemo(
    () =>
      debounce(updateStudentChunkNote, 1000, { leading: true, trailing: true }),
    [updateStudentChunkNote]
  );

  const handleRichTextChange = useCallback(
    (updatedNote: ChunkNote) => {
      track('student note saved', {
        origin: ANALYTICS_CONSTANTS.ORIGIN,
      });

      debouncedUpdateStudentChunkNote(updatedNote);
    },
    [debouncedUpdateStudentChunkNote, track]
  );

  const handleOpenNotebook = () => {
    track('open notebook clicked', {
      origin: ANALYTICS_CONSTANTS.ORIGIN,
    });
  };

  if (error) {
    return <p>Error...</p>;
  }

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h5>Enter your notes here: </h5>
        <RichText
          key={chunkId}
          studentNoteMarkdown={data?.note || null}
          onChange={handleRichTextChange}
        />
        {status && (
          <div className={styles.saveStatus}>
            <SaveStatus status={status} />
          </div>
        )}
        <div className={styles.button}>
          <Link to={!disableNotebook ? notebookUrl : ''}>
            <Button
              outline
              small
              onClick={handleOpenNotebook}
              disabled={disableNotebook}
            >
              Open notebook
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StudentNote;

interface StudentNoteProps {
  chunkId: number;
  notebookUrl: string;
  disableNotebook?: boolean;
}
